/**
 * ALTIORA · DossierTimeline — Chronologie compacte d'un dossier.
 *
 * Affiche automatiquement 4 événements clés :
 *   - Création
 *   - Dernière action
 *   - Prochaine étape
 *   - Prochaine échéance (avec criticité temporelle)
 *
 * Consommé par les pages détail dossier (opportunité, client, prospect).
 * Pas de nouvelle interface — s'intègre dans les composants existants.
 *
 * Props :
 *   - created_at    (ISO)        — obligatoire
 *   - last_action   ({at, label}) — optionnel
 *   - next_step     ({label})     — optionnel
 *   - next_deadline ({at, label, criticality?}) — optionnel
 */
import { CalendarClock, CheckCircle2, ArrowRight, AlertCircle } from "lucide-react";
import TemporalBadge from "./TemporalBadge";

const CHAMPAGNE = "#C5A880";
const GOLD = "#B98F3D";
const NAVY = "#0B2545";
const INK = "#0F172A";
const INK_SOFT = "#334155";
const SLATE = "#64748B";
const HAIRLINE = "#E8E1D0";
const GREEN = "#0B7A3B";

const REL_UNITS = [
  { s: 60,        d: 1,       u: "seconde" },
  { s: 60 * 60,   d: 60,      u: "minute" },
  { s: 3600 * 24, d: 3600,    u: "heure" },
  { s: 86400 * 7, d: 86400,   u: "jour" },
  { s: 86400 * 30, d: 86400 * 7, u: "semaine" },
  { s: Infinity,  d: 86400 * 30, u: "mois" },
];

function relative(iso) {
  if (!iso) return "";
  try {
    const then = new Date(iso).getTime();
    const now = Date.now();
    const diffSec = Math.round((now - then) / 1000);
    const abs = Math.abs(diffSec);
    for (const { s, d, u } of REL_UNITS) {
      if (abs < s) {
        const n = Math.max(1, Math.round(abs / d));
        const plural = n > 1 && u !== "mois" ? "s" : "";
        return diffSec >= 0 ? `il y a ${n} ${u}${plural}` : `dans ${n} ${u}${plural}`;
      }
    }
    return "";
  } catch { return ""; }
}


function Node({ icon: Icon, label, hint, color = CHAMPAGNE, testid, badge = null }) {
  return (
    <li className="flex items-start gap-3 py-2" data-testid={testid}>
      <div
        className="w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5"
        style={{ background: `${color}18`, color, border: `1px solid ${color}44` }}
      >
        <Icon size={11} strokeWidth={2} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-[13px] leading-tight" style={{ color: INK }}>
          {label}
        </div>
        {hint && (
          <div className="text-[10px] font-mono uppercase tracking-widest mt-0.5"
               style={{ color: SLATE }}>
            {hint}
          </div>
        )}
        {badge && <div className="mt-1.5">{badge}</div>}
      </div>
    </li>
  );
}


export default function DossierTimeline({
  createdAt,
  lastAction,
  nextStep,
  nextDeadline,
  testid = "dossier-timeline",
}) {
  return (
    <div data-testid={testid} className="rounded-lg p-4"
         style={{ background: "#FFFFFF", border: `1px solid ${HAIRLINE}` }}>
      <div className="flex items-center gap-2 mb-3">
        <CalendarClock size={13} strokeWidth={1.8} style={{ color: NAVY }} />
        <span className="text-[10px] font-mono uppercase tracking-[0.24em]" style={{ color: NAVY }}>
          Chronologie du dossier
        </span>
      </div>
      <ol className="relative pl-2 space-y-0"
          style={{ borderLeft: `1px dashed ${CHAMPAGNE}44` }}>
        <Node
          icon={CheckCircle2}
          color={CHAMPAGNE}
          label="Création"
          hint={createdAt ? relative(createdAt) : "date inconnue"}
          testid={`${testid}-created`}
        />
        {lastAction && (
          <Node
            icon={CheckCircle2}
            color={GREEN}
            label={lastAction.label || "Dernière action"}
            hint={lastAction.at ? relative(lastAction.at) : null}
            testid={`${testid}-last-action`}
          />
        )}
        {nextStep && (
          <Node
            icon={ArrowRight}
            color={GOLD}
            label={nextStep.label || "Prochaine étape"}
            hint={nextStep.hint || null}
            testid={`${testid}-next-step`}
          />
        )}
        {nextDeadline && (
          <Node
            icon={AlertCircle}
            color={GOLD}
            label={nextDeadline.label || "Prochaine échéance"}
            hint={nextDeadline.at ? relative(nextDeadline.at) : null}
            testid={`${testid}-next-deadline`}
            badge={
              nextDeadline.criticality ? (
                <TemporalBadge
                  criticality={nextDeadline.criticality}
                  criticalityLabel={nextDeadline.criticalityLabel}
                  timeToCritical={nextDeadline.timeToCritical}
                  windowLabel={nextDeadline.windowLabel}
                  testid={`${testid}-deadline-badge`}
                />
              ) : null
            }
          />
        )}
      </ol>
    </div>
  );
}
